import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { get, post } from "@/lib/api";
import {
  LayoutDashboard, BookOpen, Radio, FileText, Target, ClipboardList, CalendarDays,
  Newspaper, Bookmark, TrendingUp, CreditCard, Bell, LifeBuoy, User, LogOut, Menu, X,
  GraduationCap, PenSquare, ScanText, ListChecks, Users, ShieldCheck, Layers, TicketPercent,
  ScrollText, MessageCircleQuestion, Search, Zap, Gift, ClipboardCheck, Crown, ChevronRight, ChevronDown,
} from "lucide-react";

const NAV = {
  student: [
    { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/app/courses", label: "Courses", icon: BookOpen },
    { to: "/app/tests", label: "Test Series", icon: FileText },
    { to: "/app/pyp", label: "Previous Papers", icon: ClipboardList },
    { to: "/app/daily-quiz", label: "Daily Quiz", icon: CalendarDays },
    { to: "/app/current-affairs", label: "Current Affairs", icon: Newspaper },
    { to: "/app/saved", label: "Saved Questions", icon: Bookmark },
    { to: "/app/performance", label: "Performance", icon: TrendingUp },
    { to: "/app/subscriptions", label: "Subscriptions", icon: CreditCard },
    { to: "/app/doubts", label: "Doubts", icon: MessageCircleQuestion },
    { to: "/app/support", label: "Help & Complaints", icon: LifeBuoy },
    { to: "/app/notifications", label: "Notifications", icon: Bell },
    { to: "/app/referrals", label: "Referral & Reward", icon: Gift },
    { to: "/app/profile", label: "Profile", icon: User },
  ],
  teacher: [
    { to: "/teacher", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/teacher/courses", label: "Courses", icon: BookOpen },
    { to: "/teacher/questions", label: "Question Bank", icon: PenSquare },
    { to: "/teacher/tests", label: "Test Builder", icon: FileText },
    { to: "/teacher/mocks", label: "Mock Tests", icon: ClipboardList },
    { to: "/teacher/ocr", label: "OCR / AI Import", icon: ScanText },
    { to: "/teacher/content", label: "My Content", icon: ListChecks },
    { to: "/teacher/doubts", label: "Doubts", icon: MessageCircleQuestion },
    { to: "/teacher/copy-checking", label: "Copy Checking", icon: ClipboardCheck },
    { to: "/teacher/profile", label: "Profile", icon: User },
  ],
  admin: [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/students", label: "Students", icon: Users },
    { to: "/admin/teachers", label: "Teachers", icon: GraduationCap },
    { to: "/admin/approvals", label: "Content Approvals", icon: ShieldCheck },
    { to: "/admin/taxonomy", label: "Exam Taxonomy", icon: Layers },
    { to: "/admin/course-taxonomy", label: "Course Taxonomy", icon: BookOpen },
    { to: "/admin/coupons", label: "Coupons", icon: TicketPercent },
    { to: "/admin/plans", label: "Subscription Plans", icon: Crown },
    { to: "/admin/referrals", label: "Referral & Reward", icon: Gift },
    { to: "/admin/current-affairs", label: "Current Affairs", icon: Newspaper },
    { to: "/admin/audit", label: "Audit Logs", icon: ScrollText },
  ],
};

const ROLE_HOME = { student: "/app", teacher: "/teacher", admin: "/admin" };

function LangToggle() {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center bg-slate-100 rounded-full p-1 text-xs font-bold" data-testid="lang-toggle">
      {[["en", "EN"], ["hi", "हिं"]].map(([k, l]) => (
        <button key={k} onClick={() => setLang(k)} data-testid={`lang-${k}-btn`}
          className={`px-2.5 py-1 rounded-full ${lang === k ? "bg-white shadow text-indigo-800" : "text-slate-500"}`}>{l}</button>
      ))}
    </div>
  );
}

export default function Shell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [q, setQ] = useState("");
  const role = user?.role || "student";
  const items = NAV[role] || [];

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    if (role === "student") get("/notifications").then((d) => setUnread(d.unread)).catch(() => {});
  }, [role, loc.pathname]);

  const doLogout = async () => { await logout(); nav("/"); };
  const roleColor = { student: "bg-indigo-800", teacher: "bg-teal-700", admin: "bg-slate-900" }[role];
  const st = role === "student";

  return (
    <div className={`min-h-screen flex bg-slate-50 ${st ? "student-theme" : ""}`}>
      {/* Sidebar */}
      <aside className={`fixed z-40 lg:static inset-y-0 left-0 w-64 bg-white border-r border-slate-200 transform ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 transition-transform flex flex-col`}>
        <div className="h-16 flex items-center gap-2 px-5 border-b border-slate-200">
          <div className="h-9 w-9 rounded-xl rt-gradient flex items-center justify-center"><Zap className="h-5 w-5 text-white" /></div>
          <div>
            <p className="font-display font-extrabold text-slate-900 leading-none">RankTest</p>
            <p className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">{role} portal</p>
          </div>
          <button className="ml-auto lg:hidden" onClick={() => setOpen(false)} data-testid="sidebar-close"><X className="h-5 w-5" /></button>
        </div>
        <nav className={`flex-1 overflow-y-auto scroll-hide py-3 px-3 ${st ? "space-y-1" : "space-y-0.5"}`}>
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end}
              data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) => st
                ? `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[14px] transition-colors ${isActive ? "bg-indigo-50 text-indigo-700 font-semibold" : "text-slate-600 font-medium hover:bg-slate-50 hover:text-slate-900"}`
                : `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition ${
                  isActive ? "bg-indigo-50 text-indigo-800" : "text-slate-600 hover:bg-slate-100"}`}>
              <it.icon className={st ? "h-[18px] w-[18px]" : "h-4 w-4"} /> {it.label}
              {st && it.to === "/app/notifications" && unread > 0 && <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5" data-testid="nav-unread-badge">{unread}</span>}
            </NavLink>
          ))}
        </nav>
        {st && (
          <button onClick={() => nav("/app/subscriptions")} data-testid="upgrade-premium-card" className="mx-3 mb-2 flex items-center gap-3 text-left rounded-2xl bg-gradient-to-br from-indigo-50 to-violet-100 border border-violet-100 p-3 hover:shadow-md transition-shadow">
            <span className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 text-white flex items-center justify-center shrink-0"><Crown className="h-5 w-5" /></span>
            <span className="flex-1 min-w-0"><span className="block font-bold text-indigo-700 text-sm">Upgrade to Premium</span><span className="block text-[11px] text-slate-500 leading-snug">Unlock all courses and test series</span></span>
            <ChevronRight className="h-4 w-4 text-indigo-500 shrink-0" />
          </button>
        )}
        <div className="p-3 border-t border-slate-200">
          <button onClick={doLogout} data-testid="logout-btn"
            className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>
      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 glass border-b border-slate-200 sticky top-0 z-20 flex items-center gap-3 px-4">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-open"><Menu className="h-6 w-6" /></button>
          <form onSubmit={(e) => { e.preventDefault(); if (q.trim()) nav(`/search?q=${encodeURIComponent(q)}`); }}
            className={`hidden sm:flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-2 ${st ? "w-96 rounded-xl bg-slate-100/80" : "w-72"}`}>
            <Search className="h-4 w-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={st ? "Search courses, tests, topics..." : "Search courses, tests..."}
              data-testid="global-search-input" className="bg-transparent outline-none text-sm flex-1" />
          </form>
          <div className="ml-auto flex items-center gap-3">
            <LangToggle />
            {/* Role switcher for demo */}
            {!st && <div className="hidden md:flex items-center bg-slate-100 rounded-full p-1 text-xs font-semibold">
              {["student", "teacher", "admin"].map((r) => (
                <button key={r} data-testid={`role-switcher-${r}-btn`} onClick={() => nav(ROLE_HOME[r])}
                  className={`px-3 py-1 rounded-full capitalize ${role === r ? (st ? "bg-indigo-600 text-white shadow" : "bg-white shadow text-indigo-800") : "text-slate-500"}`}>{r}</button>
              ))}
            </div>}
            <NavLink to={role === "student" ? "/app/notifications" : "#"} className="relative" data-testid="topbar-notifications">
              <Bell className="h-5 w-5 text-slate-500" />
              {unread > 0 && <span className="absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">{unread}</span>}
            </NavLink>
            <div className="flex items-center gap-2" title={user?.name}>
              <div className={`h-9 w-9 rounded-full ${roleColor} text-white flex items-center justify-center font-bold text-sm`}>
                {(user?.name || "U").slice(0, 1).toUpperCase()}
              </div>
              {st && <span className="hidden lg:flex items-center gap-1 text-sm font-semibold text-slate-800" data-testid="topbar-user-name">{user?.name}<ChevronDown className="h-4 w-4 text-slate-400" /></span>}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 max-w-[1400px] w-full mx-auto animate-fadeUp">{children}</main>
      </div>
    </div>
  );
}
